/**
 * Workflow Target Selector Component
 * Selector for choosing workflow execution target (current user or specific users)
 * Used by workflow execution dialogs and schedule creation
 */

import { useCallback } from 'react';
import { UserSelector } from './UserSelector';
import { useI18n } from '@/i18n';
import type { WorkflowTargetType, WorkflowTarget } from '@/types/users';

export interface WorkflowTargetSelectorProps {
  value: WorkflowTarget;
  onChange: (target: WorkflowTarget) => void;
  disabled?: boolean;
  /** Current user ID (for default selection) */
  currentUserId?: string;
  /** Whether to show the user selector inline or as collapsible */
  inline?: boolean;
}

const TARGET_OPTIONS: { value: WorkflowTargetType; labelKey: string; descKey: string; label: string; desc: string }[] = [
  {
    value: 'current_user',
    labelKey: 'currentUser',
    descKey: 'currentUserDesc',
    label: '当前用户',
    desc: '使用创建者/执行者的身份运行',
  },
  {
    value: 'all_valid_users',
    labelKey: 'allValidUsers',
    descKey: 'allValidUsersDesc',
    label: '全部有效用户',
    desc: '执行时动态查询所有有效用户',
  },
  {
    value: 'specific_users',
    labelKey: 'specificUsers',
    descKey: 'specificUsersDesc',
    label: '指定用户',
    desc: '选择特定用户作为执行目标',
  },
];

export function WorkflowTargetSelector({
  value,
  onChange,
  disabled,
  currentUserId,
  inline = false,
}: WorkflowTargetSelectorProps) {
  const { messages } = useI18n();
  const t = messages.workflowTarget as Record<string, string> | undefined;

  const handleTypeChange = useCallback(
    (type: WorkflowTargetType) => {
      if (type === 'current_user' || type === 'all_valid_users') {
        // When switching to current user or all valid users, clear the user_ids
        onChange({ type, user_ids: [] });
      } else {
        // When switching to specific users, keep existing or set current user as default
        onChange({
          type,
          user_ids: value.user_ids.length > 0 ? value.user_ids : currentUserId ? [currentUserId] : [],
        });
      }
    },
    [onChange, value.user_ids, currentUserId]
  );

  const handleUserIdsChange = useCallback(
    (userIds: string[]) => {
      onChange({ ...value, user_ids: userIds });
    },
    [onChange, value]
  );

  return (
    <div className="space-y-4">
      {/* Target Type Selection */}
      <div className="space-y-2">
        {TARGET_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`
              flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
              ${value.type === option.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input
              type="radio"
              name="target-type"
              value={option.value}
              checked={value.type === option.value}
              onChange={() => handleTypeChange(option.value)}
              disabled={disabled}
              className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {t?.[option.labelKey] ?? option.label}
              </div>
              <div className="text-sm text-gray-500">
                {t?.[option.descKey] ?? option.desc}
              </div>
            </div>
          </label>
        ))}
      </div>

      {/* User Selector (shown when specific_users is selected) */}
      {value.type === 'specific_users' && (
        <div className={inline ? '' : 'mt-4 pl-7'}>
          <UserSelector
            selectedUserIds={value.user_ids}
            onChange={handleUserIdsChange}
            disabled={disabled}
          />
        </div>
      )}

      {/* Summary for current_user */}
      {value.type === 'current_user' && currentUserId && (
        <div className="mt-2 pl-7 text-sm text-gray-500">
          {t?.willRunAs ?? '将以此用户执行'}: <span className="font-mono">{currentUserId}</span>
        </div>
      )}

      {/* Summary for all_valid_users */}
      {value.type === 'all_valid_users' && (
        <div className="mt-2 pl-7 text-sm text-amber-600 bg-amber-50 p-2 rounded">
          {t?.allValidUsersNote ?? '注意: 将在执行时动态查询所有有效用户，可能影响大量用户'}
        </div>
      )}
    </div>
  );
}
